import React, { useCallback, useState } from 'react';
import classes from 'classnames';
import { Transition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import AppModal from '@/components/AppModal/AppModal';
import AppBackdrop from '@/components/AppBackdrop';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import TheButton from '@/components/school-management/TheButton/TheButton';
import { userService } from '@/services';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import commonUtils from '@/school-management/utils/common';
import userUtils from '@/utils/user';
import styles from './ResetStudentPasswordModal.module.scss';
import linkStyles from './ResetStudentPasswordLink.module.scss';

interface ResetStudentPasswordLinkProps {
  studentId: string;
  studentUsername?: string | null;
  studentName: string;
  children: React.ReactNode;
}

const splitStudentName = (name: string) => {
  const trimmed = name.trim();
  const spaceIndex = trimmed.indexOf(' ');

  if (spaceIndex === -1) {
    return { firstName: trimmed, lastName: '' };
  }

  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1).trim(),
  };
};

const ResetStudentPasswordLink: React.FC<ResetStudentPasswordLinkProps> = ({
  studentId,
  studentUsername,
  studentName,
  children,
}) => {
  const { t } = useTranslation();
  const { user } = useAppSelector(authSelector);
  const isTeacher = userUtils.isTeacher(user);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');

  const { firstName, lastName } = splitStudentName(studentName);

  const onResetStudentPassword = useCallback(async () => {
    if (!studentUsername) {
      commonUtils.showToast(t('error.somethingWentWrong'));
      return;
    }

    setIsLoading(true);
    try {
      const result = await userService.resetPasswordByTeacher(user, studentId, studentUsername);
      const tempPassword = result?.data.data.tempPassword;

      if (tempPassword) {
        setTemporaryPassword(tempPassword);
        setShowModal(true);
      } else {
        commonUtils.showToast(t('error.somethingWentWrong'));
      }
    } catch {
      commonUtils.showToast(t('error.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  }, [studentId, studentUsername, t, user]);

  const onCloseModal = useCallback(() => {
    setShowModal(false);
    setTemporaryPassword('');
  }, []);

  const onCopyTemporaryPassword = useCallback(async () => {
    if (!temporaryPassword) {
      return;
    }

    try {
      await navigator.clipboard.writeText(temporaryPassword);
    } catch {
      commonUtils.showToast(t('error.somethingWentWrong'));
      return;
    }

    onCloseModal();
  }, [temporaryPassword, onCloseModal, t]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onResetStudentPassword();
      }
    },
    [onResetStudentPassword]
  );

  if (!isTeacher) {
    return <>{children}</>;
  }

  return (
    <>
      <span
        role='button'
        tabIndex={0}
        className={classes(linkStyles.resetPasswordLink, {
          [linkStyles.resetPasswordLinkLoading]: isLoading,
        })}
        onClick={onResetStudentPassword}
        onKeyDown={onKeyDown}
        aria-disabled={isLoading}
      >
        {children}
      </span>

      <Transition in={showModal} timeout={400} mountOnEnter unmountOnExit>
        {state => (
          <>
            <AppBackdrop
              className={classes(styles.backdrop, `backdrop-${state}`)}
              emitClose={onCloseModal}
            />
            <AppModal show={true} type='fade' showBackdrop={false}>
              <div className={classes(styles.modal, `fade-${state}`)}>
                <button
                  className={styles.closeBtn}
                  onClick={onCloseModal}
                  aria-label={t('close').toString()}
                >
                  <AppIcon icon='close' size={20} />
                </button>

                <h2>{t('temporaryPassword')}</h2>
                <h2 className={styles.password}>{temporaryPassword}</h2>
                <span className={styles.description}>
                  {t('copyTemporaryPasswordDescription', {
                    firstName,
                    lastname: lastName,
                  })}
                </span>
                <span className={styles.warning}>{t('warningTemporaryPassword')}</span>

                <TheButton
                  className={styles.copyBtn}
                  text={t('copyTemporaryPassword')}
                  emitOnClick={onCopyTemporaryPassword}
                  shouldRecolor={false}
                />
              </div>
            </AppModal>
          </>
        )}
      </Transition>
    </>
  );
};

export default ResetStudentPasswordLink;
